import { useEffect, useState } from 'react'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import { makeStyles } from '@material-ui/core'
import calendar from 'src/assets/CRHealthReportImages/calendar.svg'
import {
  getMemberDataFromQr,
  getTokenForQrReport,
} from 'src/services/individualService'
import LoadingModal from 'src/components/common/LoadingModal'
import { getFullName, getQueryParams } from 'src/utils/commonMethods'
import { useLocation } from 'react-router-dom'
import formatDate from 'src/utils/commonMethods/formatDate'
import background from 'src/assets/CRHealthReportImages/background.svg'
import logo from 'src/assets/CRHealthReportImages/logo.svg'
import person from 'src/assets/CRHealthReportImages/person.svg'
import contact from 'src/assets/CRHealthReportImages/contact.svg'
import formatToINR from 'src/utils/commonMethods/formatToINR'
import { NOTIFICATION_TYPES } from 'src/utils/constants/common'
import { showNotificationMessage } from 'src/actions'
import { useDispatch } from 'react-redux'
import { NOTIFICATION_MSG_REPORTS } from 'src/utils/constants/CustomerReports'

const CRHealthReport = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const [policies, setPolicies] = useState([])
  const [memberInfo, setMemberInfo] = useState()
  const [loading, setLoading] = useState(false)
  const { search } = useLocation()
  const queryParams = getQueryParams(search)

  const fetchToken = async () => {
    setLoading(true)
    const response = await getTokenForQrReport()
    try {
      if (response?.res?.status !== 200) {
        setLoading(false)
        dispatch(
          showNotificationMessage({
            type: NOTIFICATION_TYPES.ERROR,
            message: NOTIFICATION_MSG_REPORTS?.PLEASE_TRY_AGAIN_AFTER_SOMETIME,
          })
        )
      }
      // Success
      setLoading(false)

      const token = response?.res?.data?.access_token
      // api call to fetch details
      fetchMemberDetailsInfo(token)
    } catch (err) {
      setLoading(false)
      dispatch(
        showNotificationMessage({
          type: NOTIFICATION_TYPES.ERROR,
          message: NOTIFICATION_MSG_REPORTS?.ENCOUNTERED_ERROR_ON_TOKEN_FETCH,
        })
      )
    }
  }

  const fetchMemberDetailsInfo = async (token) => {
    setLoading(true)
    const memberId = queryParams.get('memberId')
    const clientId = queryParams.get('clientId')
    const response = await getMemberDataFromQr(token, memberId, clientId)
    try {
      if (response?.res?.status !== 200) {
        setLoading(false)
        dispatch(
          showNotificationMessage({
            type: NOTIFICATION_TYPES.ERROR,
            message: NOTIFICATION_MSG_REPORTS?.PLEASE_TRY_AGAIN_AFTER_SOMETIME,
          })
        )
      }
      // Success
      setLoading(false)
      const memberDetailsData = response?.res?.data
      if (memberDetailsData?.success) {
        if (memberDetailsData?.memberData) {
          setMemberInfo(memberDetailsData?.memberData?.memberInfo)
          setPolicies(memberDetailsData?.memberData?.policies)
        }
      } else {
        dispatch(
          showNotificationMessage({
            type: NOTIFICATION_TYPES.ERROR,
            message: memberDetailsData?.message,
          })
        )
      }
    } catch (err) {
      setLoading(false)
      dispatch(
        showNotificationMessage({
          type: NOTIFICATION_TYPES.ERROR,
          message:
            NOTIFICATION_MSG_REPORTS?.ENCOUNTERED_ERROR_ON_POLICY_DETAILS,
        })
      )
    }
  }

  useEffect(() => {
    fetchToken()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getProvider = (policy) => {
    if (policy?.provider?.image) {
      return (
        <>
          <img
            alt={'Provider'}
            src={policy?.provider?.image}
            className={classes.providerImage}
          />
        </>
      )
    } else if (policy?.provider?.name) {
      return policy?.provider?.name
    } else {
      return '-'
    }
  }

  return (
    <div className={classes.container}>
      <LoadingModal loading={loading} />
      <div className={classes.logoContainer}>
        <img alt='Company Logo' src={logo}></img>
      </div>

      <div className={classes.cardContainer}>
        <Card className={classes.card}>
          <CardContent>
            <div className={classes.helpLineContentContainer}>
              <div className={classes.helplineImageContainer}>
                <img alt={'Helpline'} src={contact}></img>
              </div>
              <div className={classes.helpLineTextContainer}>
                <div className={classes.headingText}>Insurance Helpline</div>
                <div className={classes.helplineNumber}>1800 3099 247</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={classes.card}>
          <CardContent>
            <div className={classes.memberInfoContainer}>
              <div className={classes.memberInfoImageContainer}>
                <img alt={'person'} src={person}></img>
              </div>
              <div className={classes.memberInfoTextContainer}>
                {memberInfo ? (
                  <>
                    <div className={classes.memberName}>
                      {getFullName([
                        memberInfo?.firstName,
                        memberInfo?.middleName,
                        memberInfo?.lastName,
                      ])}
                    </div>
                    <div className={classes.memberIdContainer}>
                      <span className={classes.titleText}>MEMBER ID</span>
                      <span className={classes.memberIdText}>
                        {memberInfo?.empId}
                      </span>
                    </div>
                  </>
                ) : (
                  <></>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {policies?.length
          ? policies?.map((memberPolicy) => {
              return (
                <Card className={classes.card} key={'MemberPolicy'}>
                  <CardContent>
                    <div className={classes.policyDetailsContainer}>
                      <div className={classes.providerContainer}>
                        <span className={classes.policyProvider}>
                          {getProvider(memberPolicy?.policy)}
                        </span>
                      </div>
                      <span className={classes.policyTypeText}>
                        {memberPolicy?.policy?.insuranceType}
                      </span>
                      <span className={classes.policyNumberText}>
                        Pol No:&nbsp;{memberPolicy?.policy?.policyNumber ?? '-'}
                      </span>

                      <div className={classes.separatorBorder}></div>

                      <div className={classes.dateContainer}>
                        <img alt='Calendar' src={calendar}></img>

                        <div className={classes.dateText}>
                          {formatDate(memberPolicy?.policy?.issuedOn)}
                        </div>
                        <p className={classes.separatorText}> To</p>
                        <div className={classes.dateText}>
                          {formatDate(memberPolicy?.policy?.expiresOn)}
                        </div>
                      </div>
                      <div className={classes.separatorBorder}></div>
                      <span className={classes.titleText}>SUM INSURED</span>
                      <span className={classes.policyNumberText}>
                        {formatToINR(memberPolicy?.sumAssured?.text)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )
            })
          : null}
      </div>
      <p className={classes.copyrightText}>
        © Copyright 2023. All Rights Reserved.
      </p>
    </div>
  )
}

export default CRHealthReport

export const useStyles = makeStyles({
  container: {
    backgroundColor: '#A3ADB1',
    height: '100%',
    width: '100vw',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    minHeight: '100vh',
    backgroundImage: `url(${background})`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
  },
  cardContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '20px',
    flexWrap: 'wrap',
  },
  card: {
    padding: '4px',
    background: 'linear-gradient(180deg, #FFFFFF 53.77%, #F5F5F5 100%)',
    boxShadow: '0px 8px 8px rgba(0, 0, 0, 0.2)',
    borderRadius: '10px !important',
    width: '80%',
    '@media (min-width:800px)': { maxWidth: '300px', minHeight: '80px' },
  },
  headingText: {
    fontSize: '14px',
    lineHeight: '16.41px',
    color: '#000000',
  },
  helpLineTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '5px',
  },
  helplineNumber: {
    fontSize: '22px',
    lineHeight: '25.78px',
    fontWeight: 'bold',
  },
  helplineImageContainer: {
    display: 'flex',
    alignContent: 'center',
    justifyContent: 'center',
  },
  dateText: {
    fontWeight: '500',
    fontSize: '13px',
    textAlign: 'center',
    textTransform: 'uppercase',
    color: '#000000',
    padding: '10px',
  },
  logoContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '15px',
    paddingTop: '24px',
    paddingBottom: '27px',
  },
  helpLineContentContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '5px',
  },
  copyrightText: {
    fontWeight: '400',
    fontSize: '10px',
    lineHeight: '21px',
    textAlign: 'center',
    color: '#ffffff',
    width: '100%',
  },
  separatorText: {
    fontWeight: 'bold',
    fontSize: '14px',
    lineHeight: '16px',
    textAlign: 'center',
    color: '#CD8711',
  },
  dateContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '5px',
  },
  separatorBorder: {
    width: '100%',
    border: '1px solid #D4D4D4',
  },
  buttonContainer: {
    padding: '15px',
    display: 'flex',
    justifyContent: 'center',
  },
  providerImage: {
    maxHeight: '50px',
    maxWidth: '100px',
  },
  memberInfoContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  memberInfoImageContainer: {
    display: 'flex',
    alignContent: 'center',
    justifyContent: 'center',
  },
  memberInfoTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '5px',
  },
  memberName: {
    fontSize: '14px',
    fontWeight: 'bold',
    textAlign: 'center',
  },
  memberIdContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '15px',
  },
  titleText: {
    fontSize: '13px',
    color: '#818181',
  },
  memberIdText: {
    fontSize: '15px',
    textAlign: 'center',
  },
  policyDetailsContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    gap: '5px',
  },
  providerContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  policyProvider: {
    marginBottom: '15px',
  },
  policyTypeText: {
    fontSize: '13px',
    color: '#818181',
    textTransform: 'uppercase',
  },
  policyNumberText: {
    fontSize: '15px',
    fontWeight: 'bold',
  },
})
